import { useEffect } from 'react';

import { useAppDispatch } from 'hooks/store';
import { toLiveSegment } from 'hooks/useLiveTranscriptBackfill';
import { fetchAndStoreParticipants } from 'hooks/useParticipantsBackfill';
import { getCallScribeCall, getCallScribeTranscriptSegments } from 'services/callScribe';
import { lastHubEventAt } from 'services/callScribe/hubActivity';
import { applyStatusEvent, mergeTranscriptSegments } from 'store/callScribe';

export const HUB_SILENCE_MS = 8000;
export const FALLBACK_POLL_MS = 4000;

type TDispatch = ReturnType<typeof useAppDispatch>;

const settle = <T>(read: () => Promise<T>): Promise<T | undefined> =>
  Promise.resolve().then(read).catch(() => undefined);

const readLiveCall = async (
  dispatch: TDispatch,
  callId: number,
  lastStatusKey: { current: string },
): Promise<void> => {
  const [call, segments] = await Promise.all([
    settle(() => getCallScribeCall(callId)),
    settle(() => getCallScribeTranscriptSegments(callId)),
    settle(() => fetchAndStoreParticipants(dispatch as never, callId)),
  ]);

  const row = call?.data;
  if (row) {
    const status = {
      callId,
      status: row.status,
      answeredAtUtc: row.answeredAtUtc ?? null,
      endedAtUtc: row.endedAtUtc ?? null,
      durationSeconds: row.durationSeconds ?? null,
      isRecording: row.isRecording ?? null,
      recordingStoppedAtUtc: row.recordingStoppedAtUtc ?? null,
    };
    const key = JSON.stringify(status);
    if (key !== lastStatusKey.current) {
      lastStatusKey.current = key;
      dispatch(applyStatusEvent(status));
    }
  }

  const rows = segments?.data;
  if (Array.isArray(rows) && rows.length > 0) {
    dispatch(mergeTranscriptSegments({ callId, segments: rows.map(toLiveSegment) }));
  }
};

export const useHubStarvedFallback = (callId: number | undefined, live: boolean): void => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!live || typeof callId !== 'number') return undefined;
    const liveSince = Date.now();
    const lastStatusKey = { current: '' };
    let reading = false;

    const tick = (): void => {
      if (reading || document.visibilityState === 'hidden') return;
      const quietSince = Math.max(liveSince, lastHubEventAt(callId) ?? 0);
      if (Date.now() - quietSince < HUB_SILENCE_MS) return;
      reading = true;
      void readLiveCall(dispatch, callId, lastStatusKey)
        .catch(() => undefined)
        .finally(() => { reading = false; });
    };

    const timer = window.setInterval(tick, FALLBACK_POLL_MS);
    return () => { window.clearInterval(timer); };
  }, [callId, live, dispatch]);
};
